import {
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import { OrderSide, orderSideTypeCharMap } from '@tassat/common-definitions';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { Asset, Transaction } from '@tassat/fred-definitions';
import { AssetTicker, SecurityCurrencyAssetPair } from '@tassat/fred-extended-definitions';
import {
    convertToNumber, formatAsAsset, formatAsAssetNoSign, formatAsCurrency, multiplySafe
} from '@tassat/utility';
import React from 'react';

import { registerTimeout } from 'Lib/serverTimeout';
import { formatEpochDate } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { orderTimeoutAction } from 'Reducers/actions/validationActions';
import { useAppDispatch } from 'Reducers/store/hooks';

interface ITransactConfirmationProps extends WithStyles<typeof muiStyles> {
    transactValues: ITransactFormValues | Transaction;
    handleTransact: (transactValues) => void;
    cancelConfirmation: () => void;
    assetPair: SecurityCurrencyAssetPair;
    allAssets: Asset[];
    navPrice?: any;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: { padding: '0 5rem' },
    contentDivider: {
        marginTop: '.5rem',
        marginBottom: '.3rem'
    },
    amountText: {
        padding: '.5rem',
        paddingBottom: '1.5rem'
    },
    disclaimerContainer: { padding: '1.5rem 0' },
    disclaimerText: {
        flexBasis: '33%',
        paddingBottom: '1rem',
        fontSize: '14px'
    },
    table: {
        borderCollapse: 'separate' as const,
        borderSpacing: '.5rem'
    },
    tableRow: { marginBottom: '.3rem' },
    tableLabelCell: {
        backgroundColor: theme?.app?.applicationColors.tableRowLabelColor ||
            'rgba(185, 217, 236,5)',
        fontWeight: 'bold' as const
    },
    tableCell: {
        border: 'none' as const,
        paddingTop: '.3rem',
        paddingBottom: '.3rem'
    },
    submitContainer: { paddingTop: '2.5rem' },
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    }
});

export function TransactConfirmation(props: ITransactConfirmationProps) {
    const {
        classes,
        transactValues,
        handleTransact,
        cancelConfirmation,
        assetPair,
        allAssets,
        navPrice
    } = props;

    const dispatch = useAppDispatch();
    const onTimeoutError = () => dispatch(orderTimeoutAction);

    const fromApproval = (transactValues as Transaction).RequestId;

    const selectedAsset = React.useMemo<Asset | undefined>(() => {
        const assetId = fromApproval ?
            (transactValues as Transaction).AssetId
            : (transactValues as ITransactFormValues).AssetType?.value;

        return allAssets?.find(a => a.AssetId === assetId);
    }, [ allAssets, transactValues, fromApproval ]);

    const {
        OrderTypeName,
        OrderType,
        AmountWithFee,
        Amount,
        Ticker
    } = React.useMemo(() => {
        if (!fromApproval) {
            const orderType = (transactValues as ITransactFormValues).Type;

            return {
                OrderTypeName: orderSideTypeCharMap(orderType),
                OrderType: orderType,
                AmountWithFee: orderType === OrderSide.SUBSCRIPTION || orderType === OrderSide.BUY ?
                    (transactValues as ITransactFormValues).AmountWithFee
                    : `${formatAsAssetNoSign((transactValues as ITransactFormValues).Amount, selectedAsset, true)} ${selectedAsset?.Units}`,
                Amount: orderType === OrderSide.SUBSCRIPTION || orderType === OrderSide.BUY ?
                    formatAsAsset((transactValues as ITransactFormValues).Amount, assetPair.currency, 'symbol')
                    : `${formatAsAssetNoSign((transactValues as ITransactFormValues).Amount, selectedAsset, true)} ${selectedAsset?.Units}`,
                Ticker: selectedAsset?.Ticker
            };
        } else if (fromApproval) {
            const orderType = (transactValues as any).OrderSide;

            return {
                OrderTypeName: orderSideTypeCharMap(orderType),
                OrderType: orderType,
                AmountWithFee: (orderType === OrderSide.SUBSCRIPTION || orderType === OrderSide.BUY) ?
                    formatAsAsset((transactValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                    : `${formatAsAsset((transactValues as Transaction).AssetQuantity, selectedAsset, 'ticker', undefined, true)} ${selectedAsset?.Units}`,
                Amount: orderType === OrderSide.SUBSCRIPTION || orderType === OrderSide.BUY ?
                    formatAsAsset((transactValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                    : formatAsAssetNoSign((transactValues as Transaction).AssetQuantity, selectedAsset, true),
                Ticker: (transactValues as Transaction).Ticker
            };
        } else {
            return {
                OrderTypeName: '',
                OrderType: '',
                AmountWithFee: '',
                Amount: '',
                Ticker: ''
            };
        }
    }, [ transactValues, fromApproval, selectedAsset ]);

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Typography variant='body1'>
                    {(OrderType === OrderSide.SUBSCRIPTION ||
                        OrderType === OrderSide.REDEEM) ? `You are about to submit a ${OrderTypeName} to ${selectedAsset?.Description} (${selectedAsset?.Ticker}) for` : `You are about to submit a ${OrderTypeName} of`}
                </Typography>
                <Typography
                    className={classes.amountText}
                    variant='h3'
                >{AmountWithFee}
                </Typography>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                container
                direction='row'
                item
                justifyContent='space-around'
                spacing={0}
            >
                <Grid
                    container
                    direction='column'
                    item
                    lg={6}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    <Table className={classes.table}>
                        <TableHead />
                        <TableBody>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Lynq Account Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {fromApproval ?
                                        `${(transactValues as Transaction).SenderAccountName}`
                                        : `${(transactValues as ITransactFormValues).Wallet.AccountName}`}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Amount</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {Amount}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Asset Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {Ticker}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Fees</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {fromApproval ?
                                        formatAsCurrency((transactValues as any).FeeAmount)
                                        : formatAsCurrency((transactValues as ITransactFormValues).Fee)}
                                </TableCell>
                            </TableRow>
                            {(OrderType === OrderSide.SUBSCRIPTION || OrderType === OrderSide.REDEEM) &&
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>NAV</TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {formatAsCurrency(navPrice?.Price)}
                                    </TableCell>
                                </TableRow>}
                            {(OrderType === OrderSide.SUBSCRIPTION || OrderType === OrderSide.REDEEM) &&
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>{`Total Estimated ${OrderType === OrderSide.SUBSCRIPTION ? 'Subscription Amount' : 'Proceeds'}`}</TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {fromApproval ?
                                        formatAsAsset((transactValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                                        : formatAsAsset(multiplySafe(convertToNumber((transactValues as ITransactFormValues).AmountWithFee), navPrice?.Price), assetPair.currency, 'symbol')}
                                    </TableCell>
                                </TableRow>}
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Private Description</TableCell>
                                <TableCell className={classes.tableCell}>{transactValues.PrivateDescription}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Timestamp</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {fromApproval ?
                                        formatEpochDate((transactValues as Transaction).AuditTime)
                                        : formatEpochDate((transactValues as ITransactFormValues).RequestTime)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
                <Grid
                    className={classes.disclaimerContainer}
                    container
                    direction='column'
                    item
                    justifyContent='center'
                    lg={4}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    {(OrderType === OrderSide.SUBSCRIPTION ||
                        OrderType === OrderSide.REDEEM) &&
                        <>
                            <Typography
                                className={classes.disclaimerText}
                                variant='body1'
                            >
                                {`Please review the details of your ${OrderType === OrderSide.SUBSCRIPTION ? 'subscription' : 'redemption'} of shares of ${selectedAsset?.Description} (${selectedAsset?.Ticker}). Once you confirm, your ${OrderType === OrderSide.SUBSCRIPTION ? 'subscription' : 'redemption'} will remain pending until your shares are ${OrderType === OrderSide.SUBSCRIPTION ? 'issued' : 'redeemed'} by ${selectedAsset?.Ticker} at the latest NAV.  ${selectedAsset?.Ticker === AssetTicker.TFND ? `The administrator of ${selectedAsset?.Ticker} will calculate the fund’s NAV at least once each trading day (or with such other frequency as the fund's board, in consultation with its investment adviser, may determine).` : ''}`}
                            </Typography>
                        </>}
                    {OrderType === OrderSide.SELL &&
                        <>
                            <Typography
                                className={classes.disclaimerText}
                                variant='body1'
                            >
                                Please review the details of your sell order. All orders are executed at the most recent net asset
                                value as provided by the fund.
                            </Typography>
                        </>}
                    {OrderType === OrderSide.BUY &&
                        <>
                            <Typography
                                className={classes.disclaimerText}
                                variant='body1'
                            >
                                Please review the details of your buy order. All orders are executed at the most recent net asset value
                                as provided by the fund.
                            </Typography>
                        </>}
                </Grid>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                alignItems='center'
                className={classes.submitContainer}
                container
                direction='column'
                item
                spacing={0}
            >
                <SubmitButton
                    onClick={() => {
                        handleTransact(transactValues);
                        registerTimeout(onTimeoutError, 'order');
                    }}
                    type={`${OrderTypeName}-confirm`}
                />
                <Grid item>
                    <Typography
                        className={classes.cancelButton}
                        onClick={cancelConfirmation}
                        variant='h4'
                    >CANCEL
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default withStyles(muiStyles)(TransactConfirmation);